import SoulMapReadyModal from '@/components/soul-map-ready-modal';
import { useActiveTenantScopeSync } from '@/hooks/useActiveTenantScopeSync';
import { useBackendSessionValidation } from '@/hooks/useBackendSessionValidation';
import { isSoulMapMode } from '@/utils/app-mode';
import {
  rememberLastTenantSlug,
  resolveTenantBySlug,
} from '@/utils/active-tenant';
import { isBackendSessionRecentlyValidated } from '@/utils/auth-session';
import {
  markPostLoginReturnPath,
  settlePostLoginReturnPath,
} from '@/utils/post-login-return-path';
import {
  getSoulMapConsumerLandingPath,
  isSoulMapAppRoute,
  isSoulMapLoginFlow,
  isSoulMapLoginPage,
} from '@/utils/soulmap-auth';
import { useAuth0 } from '@auth0/auth0-react';
import { Spinner } from '@nextui-org/react';
import { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useAuthStore } from '../store/use-auth-store';

/** The user reached the app, so any link remembered for after login is done with. */
const PostLoginReturnPathSettled: React.FC = () => {
  useEffect(() => {
    settlePostLoginReturnPath();
  }, []);

  return null;
};

const PrivateRoutes: React.FC = () => {
  const sessionAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const showTenantSelector = useAuthStore((state) => state.showTenantSelector);
  const tenants = useAuthStore((state) => state.tenants);
  const { isAuthenticated } = useAuth0();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const location = useLocation();

  useEffect(() => {
    if (resolveTenantBySlug(tenantSlug, tenants)) {
      rememberLastTenantSlug(tenantSlug!);
    }
  }, [tenantSlug, tenants]);

  useActiveTenantScopeSync(sessionAuthenticated && !showTenantSelector);

  const soulMapAppRoute = isSoulMapAppRoute(location.pathname);
  const shouldValidateBackendSession =
    sessionAuthenticated && !isBackendSessionRecentlyValidated();
  const backendSessionStatus = useBackendSessionValidation(
    shouldValidateBackendSession
  );
  const loginPath =
    isSoulMapLoginFlow() ||
    isSoulMapMode() ||
    soulMapAppRoute ||
    isSoulMapLoginPage(location.pathname)
      ? getSoulMapConsumerLandingPath()
      : '/login';

  // Signed out (or the session had expired) on a freshly opened in-app URL,
  // e.g. a practice link from an email: remember it so login can return here.
  const rememberReturnPath = () => {
    if (loginPath === '/login') {
      markPostLoginReturnPath(`${location.pathname}${location.search}`);
    }
  };

  if (
    (!sessionAuthenticated && !isAuthenticated) ||
    (!isSoulMapLoginFlow() && !isSoulMapMode() && showTenantSelector)
  ) {
    if (!sessionAuthenticated && !isAuthenticated) {
      rememberReturnPath();
    }
    return <Navigate to={loginPath} replace />;
  }

  // Full-screen gate only on the first /auth/me/ check this mount — not on
  // periodic re-validation (handled silently by useBackendSessionValidation).
  if (
    sessionAuthenticated &&
    shouldValidateBackendSession &&
    backendSessionStatus === 'checking'
  ) {
    return (
      <div className="flex h-screen items-center justify-center bg-white dark:bg-gray-900">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  if (
    sessionAuthenticated &&
    shouldValidateBackendSession &&
    backendSessionStatus === 'invalid'
  ) {
    rememberReturnPath();
    return <Navigate to={loginPath} replace />;
  }

  if (isSoulMapLoginFlow()) {
    return <Navigate to={getSoulMapConsumerLandingPath()} replace />;
  }

  if (isSoulMapMode()) {
    if (!tenantSlug && tenants?.[0]?.slug) {
      return <Navigate to={`/${tenants[0].slug}/soulmap`} replace />;
    }

    return (
      <>
        <SoulMapReadyModal />
        <Outlet />
      </>
    );
  }

  // If authenticated but no tenant slug, redirect to tenant selection
  if (!tenantSlug && tenants && tenants.length > 0) {
    if (tenants.length === 1) {
      const defaultPath = 'leadership-gym';
      return <Navigate to={`/${tenants[0].slug}/${defaultPath}`} replace />;
    }
    return (
      <Navigate to="/login" state={{ showTenantSelector: true }} replace />
    );
  }

  // If tenant slug doesn't match any available tenants, fix URL or show tenant selector
  if (tenantSlug && tenants && tenants.length > 0) {
    const validTenant = tenants.find((t) => t.slug === tenantSlug);
    if (!validTenant) {
      if (tenants.length === 1) {
        const pathSuffix =
          location.pathname.replace(/^\/[^/]+/, '') || '/home';
        return (
          <Navigate
            to={`/${tenants[0].slug}${pathSuffix}${location.search}`}
            replace
          />
        );
      }

      return (
        <Navigate to="/login" state={{ showTenantSelector: true }} replace />
      );
    }
  }

  return (
    <>
      <PostLoginReturnPathSettled />
      <SoulMapReadyModal />
      <Outlet />
    </>
  );
};

export default PrivateRoutes;
